/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { rankMetrics } from "./useDashboardMetrics";
import MetricPickGrid from "./MetricPickGrid";
import { IcClose } from "./icons";

/**
 * Centered glass modal for adding metrics to an existing dashboard. When a
 * layout template is pending, a counter shows how many slots it fits.
 */
export default function AddMetricModal({ open, metrics, getSummary, template, onAdd, onAddCombined, onClose }) {
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const ranked = useMemo(() => rankMetrics(metrics, getSummary), [metrics, getSummary]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const count = selected.size;
  const slots = template?.slots?.length || 0;

  const confirm = () => {
    if (!count) return;
    // Fill template slots in the ranked order shown, so the strongest pick
    // lands in the first (biggest) slot.
    onAdd(ranked.filter((m) => selected.has(m.id)).map((m) => m.id));
  };

  return (
    <div
      className="db-modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="db-modal db-modal-wide" role="dialog" aria-modal="true" aria-label="Add metrics">
        <div className="db-modal-inner">
          <div className="db-modal-head">
            <div>
              <div className="db-modal-t">Add metrics</div>
              <div className="db-modal-s">
                {template
                  ? `${template.name} looks best with ${slots} metrics.`
                  : "Pick the company metrics you want to chart on this dashboard."}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {template && (
                <span className={`db-slotpill ${count >= slots ? "full" : ""}`}>
                  {count} of {slots} picked
                </span>
              )}
              <button type="button" className="db-iconbtn" onClick={onClose} aria-label="Close">
                <IcClose />
              </button>
            </div>
          </div>

          {onAddCombined && !template && metrics.length > 1 && (
            <button type="button" className="db-combine-tile" onClick={onAddCombined}>
              <span className="db-combine-sym" aria-hidden="true">÷</span>
              <span className="db-combine-tx">
                <span className="t">Combine two metrics</span>
                <span className="d">One card calculated from two — like Revenue ÷ Hours worked</span>
              </span>
              <span className="db-combine-go">Set up</span>
            </button>
          )}

          <MetricPickGrid
            metrics={ranked}
            getSummary={getSummary}
            selected={selected}
            onToggle={toggle}
          />

          <div className="db-modal-foot">
            <button type="button" className="db-btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="db-btn primary" disabled={!count} onClick={confirm}>
              {count === 1 ? "Add 1 metric" : `Add ${count} metrics`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

AddMetricModal.propTypes = {
  open: PropTypes.bool,
  metrics: PropTypes.array,
  getSummary: PropTypes.func.isRequired,
  template: PropTypes.object,
  onAdd: PropTypes.func.isRequired,
  onAddCombined: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};
